import React, { useState, useCallback } from 'react';
import '../styles/SanskritKeyboard.css';

// Display mapping for svara keys: show base vowel अ with combining mark
const SVARA_DISPLAY_TEXT: Record<string, string> = {
  '\ue302': 'अ\ue302', // svarita
  '\ue301': 'अ\ue301', // anudatta
  '\ue303': 'अ\ue303', // deergha svarita
};

interface KeyboardPage {
  name: string;
  keys: string[][];
}

// Special keys for page switching and modifiers
const PAGE_SWITCH_NUMBERS = '१२३';
const PAGE_SWITCH_ABC = 'अआ';
const SHIFT_KEY = '⇧';

// Inscript keyboard layout based on Vakya keyboard
const KEYBOARD_PAGES: KeyboardPage[] = [
  {
    name: 'अ',
    keys: [
      // Vedic svara marks row
      ['\ue302', '\ue301', '\ue303'],
      // Number row (Inscript)
      ['ॊ', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'ॅ', '⌫'],
      // QWERTY row
      ['ौ', 'ै', 'ा', 'ी', 'ू', 'ब', 'ह', 'ग', 'द', 'ज', 'ड', '़'],
      // ASDF row with Enter at end
      ['ो', 'े', '्', 'ि', 'ु', 'प', 'र', 'क', 'त', 'च', 'ट', '↵'],
      // ZXCV row with Shift at start, Numbers key at end
      [SHIFT_KEY, 'ॆ', 'ं', 'म', 'न', 'व', 'ल', 'स', ',', '.', 'य', PAGE_SWITCH_NUMBERS],
    ],
  },
  {
    name: 'आ',
    keys: [
      // Vedic svara marks row
      ['\ue302', '\ue301', '\ue303'],
      // Number row shifted
      ['ओ', '!', '@', '#', 'ञ', 'त्र', 'क्ष', 'श्र', '(', ')', 'ः', 'ऋ', '⌫'],
      // QWERTY row shifted
      ['औ', 'ऐ', 'आ', 'ई', 'ऊ', 'भ', 'ङ', 'घ', 'ध', 'झ', 'ढ', 'ऱ'],
      // ASDF row shifted with Enter at end
      ['ओ', 'ए', 'अ', 'इ', 'उ', 'फ', 'ळ', 'ख', 'थ', 'छ', 'ठ', '↵'],
      // ZXCV row with Shift at start, Numbers key at end
      [SHIFT_KEY, 'ऎ', 'ँ', 'ण', 'ॠ', 'ज्ञ', 'ळ', 'श', 'ष', '।', '॥', PAGE_SWITCH_NUMBERS],
    ],
  },
  {
    name: '१',
    keys: [
      // Vedic svara marks row
      ['\ue302', '\ue301', '\ue303'],
      // Devanagari numerals
      ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९', 'ॐ', 'ऽ', '⌫'],
      // Punctuation and extras
      ['[', ']', '{', '}', '/', '\\', '|', "'", '"', ';', ':', '॰'],
      // More characters with Enter at end
      ['क़', 'ख़', 'ग़', 'ज़', 'ड़', 'ढ़', 'फ़', 'य़', 'ॡ', 'ॢ', 'ॣ', '↵'],
      // Additional with Shift at start, ABC key at end
      [SHIFT_KEY, 'ॄ', '₹', '+', '-', '=', '*', '%', '&', '^', '~', PAGE_SWITCH_ABC],
    ],
  },
];

interface SanskritKeyboardProps {
  visible: boolean;
  onKeyPress: (key: string) => void;
  onEnter: () => void;
  onClose: () => void;
  scaledFontSize: (baseSize: number) => number;
}

const SanskritKeyboard: React.FC<SanskritKeyboardProps> = ({
  visible,
  onKeyPress,
  onEnter,
  onClose,
  scaledFontSize,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [previousPage, setPreviousPage] = useState(0); // Remember page before going to numbers

  const handleKeyPress = useCallback((key: string) => {
    if (key === '⌫') {
      onKeyPress('backspace');
    } else if (key === '↵') {
      onEnter();
    } else if (key === SHIFT_KEY) {
      // Toggle between page 0 and 1 (अ and आ)
      if (currentPage === 2) {
        // If on numbers page, go back to previous letter page and toggle
        setCurrentPage(previousPage === 0 ? 1 : 0);
      } else {
        setCurrentPage(prev => prev === 0 ? 1 : 0);
      }
    } else if (key === PAGE_SWITCH_NUMBERS) {
      // Switch to numbers page
      setPreviousPage(currentPage);
      setCurrentPage(2);
    } else if (key === PAGE_SWITCH_ABC) {
      // Switch back from numbers page
      setCurrentPage(previousPage);
    } else if (key && key.trim()) {
      onKeyPress(key);
    }
  }, [onKeyPress, onEnter, currentPage, previousPage]);

  const getKeyClassName = (key: string, rowIndex: number): string => {
    const classes = ['keyboard-key'];

    if (key === '⌫') {
      classes.push('backspace-key');
    } else if (key === '↵') {
      classes.push('enter-key');
    } else if (key === SHIFT_KEY) {
      classes.push('shift-key');
      if (currentPage === 1) {
        classes.push('shift-active');
      }
    } else if (key === PAGE_SWITCH_NUMBERS || key === PAGE_SWITCH_ABC) {
      classes.push('numbers-key');
    } else if (SVARA_DISPLAY_TEXT[key]) {
      classes.push('svara-key');
    } else if (rowIndex === 0) {
      classes.push('svara-row-key');
    }

    return classes.join(' ');
  };

  const renderKeyContent = (key: string): React.ReactNode => {
    if (SVARA_DISPLAY_TEXT[key]) {
      return <span className="key-text">{SVARA_DISPLAY_TEXT[key]}</span>;
    }
    return <span className="key-text">{key}</span>;
  };

  const getRowClassName = (rowIndex: number): string => {
    const classes = ['key-row'];
    if (rowIndex === 0) {
      classes.push('svara-row');
    }
    return classes.join(' ');
  };

  if (!visible) {
    return null;
  }

  const keyStyle = {
    fontSize: scaledFontSize(20),
    minHeight: scaledFontSize(44),
  };

  return (
    <div className="sanskrit-keyboard">
      {/* Close button */}
      <button
        className="keyboard-close-button"
        onClick={onClose}
        aria-label="Close keyboard"
        title="Close keyboard"
      >
        ✕
      </button>
      {/* Main keys */}
      <div className="keys-container">
        {KEYBOARD_PAGES[currentPage].keys.map((row, rowIndex) => (
          <div key={rowIndex} className={getRowClassName(rowIndex)}>
            {row.map((key, keyIndex) => (
              <button
                key={keyIndex}
                className={getKeyClassName(key, rowIndex)}
                onClick={() => handleKeyPress(key)}
                aria-label={key === '⌫' ? 'Backspace' : key}
                style={keyStyle}
              >
                {renderKeyContent(key)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SanskritKeyboard;
